/**
 * Zod schemas for the three-agent pipeline.
 *
 * These are PORTED FROM copapp-sim-15's working schemas — not invented.
 * They match the actual JSON structures that Grok returns successfully.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared: World ID pattern (person p:xxx or object o:xxx)
// ---------------------------------------------------------------------------
export const worldIdSchema = z.union([
    z.string().regex(/^p:/),
    z.string().regex(/^o:/),
]);
// ---------------------------------------------------------------------------
// Dialog Agent Output (from sim-15 dialog.ts — PROVEN WORKING)
// ---------------------------------------------------------------------------
export const dialogOutputSchema = z.object({
    narrationShort: z.string().max(240),
    utterances: z.array(z.object({
        speakerId: z.string().regex(/^npc-\d+$/),
        referentPersonId: worldIdSchema.optional(),
        content: z.string(),
        voice: z.object({
            pace: z.enum(['slow', 'normal', 'fast']),
            volume: z.enum(['low', 'normal', 'raised', 'shouting']),
            tone: z.string(),
        }),
    })),
    disclosures: z.array(z.object({
        kind: z.enum(['name', 'id', 'fact', 'location']),
        targetId: worldIdSchema.optional(),
        value: z.string(),
    })),
    exposedCues: z.array(z.string()),
    speakerMap: z.record(z.string().regex(/^npc-\d+$/), z.object({
        personId: worldIdSchema.optional(),
        label: z.string(),
    })),
});
// ---------------------------------------------------------------------------
// World Agent Output (from sim-15 world.ts — PROVEN WORKING)
// ---------------------------------------------------------------------------
export const patchOperationSchema = z.object({
    op: z.enum(['add', 'remove', 'replace']),
    path: z.string(),
    value: z.unknown().optional(),
});
export const timelineEntrySchema = z.object({
    t: z.string(),
    event: z.string(),
    tags: z.array(z.string()).optional(),
    ref: z.array(z.string()).optional(),
});
export const worldOutputSchema = z.object({
    simulationTimeElapsed: z.string(),
    patch: z.array(patchOperationSchema),
    timeline: z.array(timelineEntrySchema).optional(),
});
// ---------------------------------------------------------------------------
// World State (from sim-15 world.ts — PROVEN WORKING)
// ---------------------------------------------------------------------------
export const worldPublicSchema = z.object({
    people: z.record(worldIdSchema, z.object({
        label: z.string().optional(),
        personId: z.string().optional(),
        posture: z.string().optional(),
        hands: z.string().optional(),
        affect: z.string().optional(),
        speech: z.object({
            pace: z.enum(['slow', 'normal', 'fast']).optional(),
            volume: z.enum(['low', 'normal', 'raised', 'shouting']).optional(),
            tone: z.string().optional(),
        }).optional(),
        location: z.object({
            x: z.number().optional(),
            y: z.number().optional(),
            zone: z.string().optional(),
        }).optional(),
        visibleItems: z.array(z.string()).optional(),
        proximity: z.enum(['visible', 'nearby', 'mentioned']).optional(),
    })),
    objects: z.record(worldIdSchema, z.object({
        kind: z.string(),
        state: z.record(z.string(), z.unknown()).optional(),
        location: z.object({
            x: z.number().optional(),
            y: z.number().optional(),
            zone: z.string().optional(),
        }).optional(),
        discovered: z.boolean().optional(),
        category: z.enum(['item', 'location', 'detail']).optional(),
    })),
    ambient: z.object({
        weather: z.string().optional(),
        timeOfDay: z.string().optional(),
        lighting: z.string().optional(),
        noise: z.array(z.string()).optional(),
        hazards: z.array(z.string()).optional(),
    }),
    sensory: z.object({
        visual: z.array(z.string()),
        auditory: z.array(z.string()),
        olfactory: z.array(z.string()),
        tactile: z.array(z.string()).optional(),
    }),
    timeline: z.array(timelineEntrySchema),
});
export const worldInternalSchema = z.object({
    secrets: z.array(z.string()),
    undisclosed: z.record(z.string(), z.record(z.string(), z.unknown())),
    seed: z.string(),
});
export const worldStateSchema = z.object({
    public: worldPublicSchema,
    internal: worldInternalSchema,
});
// ---------------------------------------------------------------------------
// Psyche Agent Output (from sim-15 psyche.ts — PROVEN WORKING)
// ---------------------------------------------------------------------------
export const psycheProfileSchema = z.object({
    officerStyle: z.string(),
    stressIndicators: z.array(z.string()),
    communicationNotes: z.array(z.string()),
    tacticalNotes: z.array(z.string()),
    riskLevel: z.enum(['low', 'moderate', 'elevated', 'high']),
    guidance: z.object({
        difficultyNudge: z.union([
            z.literal(-2), z.literal(-1), z.literal(0), z.literal(1), z.literal(2),
        ]),
        suggestedPressures: z.array(z.string()),
        guardrails: z.array(z.string()),
        suggestedCuesNext: z.array(z.string()),
    }),
    hypotheses: z.array(z.object({
        id: z.string(),
        description: z.string(),
        posterior: z.number().min(0).max(1),
        probes: z.array(z.string()),
        evidence: z.array(z.string()),
    })),
    skillRatings: z.record(z.string(), z.number()),
    turnSummary: z.string(),
});
//# sourceMappingURL=simulation.js.map